import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Col, Row } from 'antd'
import { notify } from 'reapop'
import * as yup from 'yup'

import { createConfiguration, updateConfiguration } from '../../../api/configuration'

import HookedField from '../../HookedField'
import Button from '../../Button'
import Icon from '../../Icon'
import { ICONS, PERMISSIONS } from '../../../utils/constants'

import styles from './ConfigurationForm.module.scss'
import useClient from '../../../hooks/useClient'

// Configuration validation schema
const configurationSchema = yup.object().shape({
  key: yup
    .string()
    .required('This field is required')
    .min(2, 'Key must be at least 2 characters')
    .max(255, 'Key must be less than 255 characters')
    .matches(/^[a-zA-Z0-9_.-]+$/, 'Key can only contain letters, numbers, dots, hyphens and underscores'),
  valueEn: yup
    .string()
    .required('This field is required')
    .min(1, 'Value in English is required'),
  valueAr: yup
    .string()
    .required('This field is required')
    .min(1, 'Value in Arabic is required'),
  description: yup
    .string()
    .max(500, 'Description must be less than 500 characters'),
  isActive: yup.boolean(),
})


const ConfigurationForm = ({ defaultValues }) => {
  const { client, isClientCan } = useClient()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isClientCanUpdateConfigurations = isClientCan(PERMISSIONS.UPDATE_CONFIGURATIONS)
  const isClientCanCreateConfigurations = isClientCan(PERMISSIONS.CREATE_CONFIGURATIONS)

  const createConfigurationMutation = useMutation(createConfiguration, {
    onSuccess: () => {
      navigate('/main/configuration')
      dispatch(
        notify({
          title: t('configuration.messages.created'),
          status: 'success',
          dismissAfter: 2000,
        })
      )
    },
    onError: (error) => {
      if (error?.response?.data?.messages?.length) {
        const errorMessage = error?.response?.data?.messages[0].message
        dispatch(
          notify({
            title: t(`configuration.error.${errorMessage}`) || errorMessage,
            status: 'error',
            dismissAfter: 4000,
          })
        )
      } else {
        dispatch(
          notify({
            title: t('configuration.messages.createFailed'),
            status: 'error',
            dismissAfter: 4000,
          })
        )
      }
    },
  })

  const updateConfigurationMutation = useMutation(updateConfiguration, {
    onSuccess: () => {
      navigate('/main/configuration')
      dispatch(
        notify({
          title: t('configuration.messages.updated'),
          status: 'success',
          dismissAfter: 2000,
        })
      )
    },
    onError: (error) => {
      if (error?.response?.data?.messages?.length) {
        const errorMessage = error?.response?.data?.messages[0].message
        dispatch(
          notify({
            title: t(`configuration.error.${errorMessage}`) || errorMessage,
            status: 'error',
            dismissAfter: 4000,
          })
        )
      } else {
        dispatch(
          notify({
            title: t('configuration.messages.updateFailed'),
            status: 'error',
            dismissAfter: 4000,
          })
        )
      }
    },
  })

  const methods = useForm({
    mode: 'onSubmit',
    shouldFocusError: true,
    resolver: yupResolver(configurationSchema),
    defaultValues: defaultValues || {
      isActive: true
    }
  })

  // Update form values when defaultValues changes
  useEffect(() => {
    if (defaultValues) {
      Object.keys(defaultValues).forEach(key => {
        methods.setValue(key, defaultValues[key])
      })
    }
  }, [defaultValues, methods])

  const submitHandler = useCallback(
    async (formData) => {
      const configurationMutation = defaultValues ? updateConfigurationMutation : createConfigurationMutation

      if (defaultValues) {
        formData.id = defaultValues.id
      }

      await configurationMutation.mutateAsync(formData)
    },
    [createConfigurationMutation, updateConfigurationMutation, defaultValues]
  )

  const canEdit = defaultValues ? isClientCanUpdateConfigurations : isClientCanCreateConfigurations

  return (
    <div className={styles.configurationForm}>
      <FormProvider {...methods}>
        <form className="form" autoComplete="off" onSubmit={methods.handleSubmit(submitHandler)}>
          <div className="form-title">{t('configuration.form.title')}</div>

          {defaultValues && (
            <HookedField
              name="id"
              defaultValue={defaultValues.id}
              fieldType="hiddenField"
              type="hidden"
            />
          )}

          <Row gutter={[20, 5]}>
            <Col span={24} lg={{ span: 12 }}>
              <HookedField
                disabled={!canEdit || Boolean(defaultValues)} // Key should not be editable when updating
                fieldType="textField"
                isRequired
                label={t('configuration.form.key')}
                name="key"
                placeholder={t('configuration.form.keyPlaceholder')}
                type="text"
                defaultValue={defaultValues?.key || ''}
              />
            </Col>
            <Col span={24} lg={{ span: 12 }}>
              <HookedField
                disabled={!canEdit}
                fieldType="textField"
                isRequired
                label={t('configuration.form.valueEn')}
                name="valueEn"
                placeholder={t('configuration.form.valueEnPlaceholder')}
                type="text"
                defaultValue={defaultValues?.valueEn || ''}
              />
            </Col>
            <Col span={24} lg={{ span: 12 }}>
              <HookedField
                disabled={!canEdit}
                fieldType="textField"
                isRequired
                label={t('configuration.form.valueAr')}
                name="valueAr"
                placeholder={t('configuration.form.valueArPlaceholder')}
                type="text"
                defaultValue={defaultValues?.valueAr || ''}
              />
            </Col>
            <Col span={24}>
              <HookedField
                disabled={!canEdit}
                fieldType="textareaField"
                label={t('configuration.form.description')}
                name="description"
                placeholder={t('configuration.form.descriptionPlaceholder')}
                rows={3}
                defaultValue={defaultValues?.description || ''}
              />
            </Col>
            <Col span={24} lg={{ span: 12 }}>
              <div className="form-visibility">
                <Icon className="form-visibility-icon" icon={ICONS.EYE} size={24} />
                <span className="form-visibility-text">{t('configuration.form.isActive')}</span>
                <HookedField
                  disabled={!canEdit}
                  className="form-visibility-switch"
                  name="isActive"
                  fieldType="customField"
                  type="switch"
                  defaultValue={defaultValues?.isActive ?? true}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={[20, 5]} className={styles.actions}>
            <Col span={24} lg={{ span: 12 }}>
              <Button
                className={styles.saveBtn}
                disabled={!methods.formState.isDirty || !canEdit}
                size="large"
                type="primary"
                htmlType="submit"
                loading={createConfigurationMutation.isLoading || updateConfigurationMutation.isLoading}
              >
                {t('app.save')}
              </Button>
            </Col>
            <Col span={24} lg={{ span: 12 }}>
              <Button
                className={styles.cancelBtn}
                size="large"
                onClick={() => navigate('/main/configuration')}
              >
                {t('app.cancel')}
              </Button>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </div>
  )
}

export default ConfigurationForm
